import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getCampaigns, getCopyHistory, getAgentConfig } from '../lib/db'
import { authFetch } from '../lib/api'
import { ArrowRight, X, Check, Sparkles, User, Plug, Wand2, Rocket, Bot } from 'lucide-react'

const SERVER = import.meta.env.VITE_API_URL || ''
const DISMISS_KEY = 'clixyads_tour_dismissed'

const STEPS = [
  {
    id: 'profile',
    icon: User,
    title: 'Completa tu perfil',
    desc: 'La IA escribe mejor cuando sabe de ti. Rellena sector, ciudad y descripción del negocio.',
    cta: 'Ir al perfil',
    to: '/profile',
  },
  {
    id: 'connect',
    icon: Plug,
    title: 'Conecta Meta o Google',
    desc: 'Para lanzar campañas reales y recibir mensajes en el inbox.',
    cta: 'Conectar plataformas',
    to: '/connections',
  },
  {
    id: 'copy',
    icon: Wand2,
    title: 'Genera tu primer copy con IA',
    desc: '3 variaciones listas en 5 segundos para Meta, Google o TikTok.',
    cta: 'Probar generador',
    to: '/copy-generator',
  },
  {
    id: 'campaign',
    icon: Rocket,
    title: 'Lanza tu primera campaña',
    desc: 'Con todo lo anterior hecho, tu primer anuncio sale en minutos.',
    cta: 'Crear campaña',
    to: '/campaigns/new',
  },
  {
    id: 'agent',
    icon: Bot,
    title: 'Activa el Agente IA',
    desc: 'Responde DMs de Instagram, Messenger y WhatsApp automáticamente, 24/7.',
    cta: 'Configurar agente',
    to: '/ai-agent',
    requiresPlan: ['pro', 'business'],
  },
]

function profileCompleted(profile) {
  if (!profile) return false
  return Boolean(profile.bio && profile.sector && profile.city)
}

export function OnboardingTour() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [checkedSteps, setCheckedSteps] = useState({
    profile: false,
    connect: false,
    copy: false,
    campaign: false,
    agent: false,
  })
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === 'true')
  const [minimized, setMinimized] = useState(false)

  // Recarga el estado real al montar o cambiar de ruta (para detectar completados)
  useEffect(() => {
    if (!user?.id) return
    // Skip el check si estamos en rutas donde el tour está oculto de todos modos.
    // Evita spam de peticiones + re-renders innecesarios (y perder focus en inputs).
    const hiddenRoutes = ['/onboarding', '/login', '/register', '/privacy-policy', '/terms', '/data-deletion']
    if (hiddenRoutes.some(p => location.pathname.startsWith(p))) return
    let alive = true

    const check = async () => {
      try {
        const [campaigns, copies, config, connStatus] = await Promise.all([
          getCampaigns(user.id).catch(() => []),
          getCopyHistory(user.id).catch(() => []),
          getAgentConfig(user.id).catch(() => null),
          authFetch(`/api/connections/status`).then(r => r.json()).catch(() => ({})),
        ])
        if (!alive) return
        setCheckedSteps({
          profile: profileCompleted(profile),
          connect: Boolean(connStatus?.meta?.connected || connStatus?.google?.connected || connStatus?.tiktok?.connected),
          copy: Array.isArray(copies) && copies.length > 0,
          campaign: Array.isArray(campaigns) && campaigns.length > 0,
          agent: Boolean(config?.enabled),
        })
      } catch {}
    }
    check()
    return () => { alive = false }
  }, [user?.id, profile?.sector, profile?.bio, profile?.city, location.pathname])

  const plan = profile?.plan || 'free'
  const visibleSteps = useMemo(
    () => STEPS.filter(s => !s.requiresPlan || s.requiresPlan.includes(plan)),
    [plan]
  )

  const completedCount = visibleSteps.filter(s => checkedSteps[s.id]).length
  const allDone = completedCount === visibleSteps.length
  const currentStep = visibleSteps.find(s => !checkedSteps[s.id])

  // No mostrar en onboarding inicial, auth, ni en pantallas de creación/configuración donde estorba visualmente.
  const hiddenOnRoute = [
    '/onboarding', '/login', '/register',
    '/privacy-policy', '/terms', '/data-deletion',
    '/studio/setup', '/landings/new', '/landings/', '/studio/create', '/studio/post',
    '/profile',
  ].some(p => location.pathname.startsWith(p))

  if (!user || !profile || dismissed || allDone || hiddenOnRoute || !currentStep) return null

  const Icon = currentStep.icon

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-4 right-4 z-40 w-14 h-14 rounded-full aurora-gradient shadow-[0_10px_30px_-10px_rgba(139,92,246,0.6)] flex items-center justify-center cyan-pulse"
        aria-label="Reanudar tour"
      >
        <Sparkles size={20} className="text-white" />
        <span className="absolute -top-1 -right-1 bg-aurora-cyan text-ink rounded-full text-[10px] font-mono-aurora font-bold w-5 h-5 flex items-center justify-center">
          {visibleSteps.length - completedCount}
        </span>
      </button>
    )
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px] z-40">
      <div
        className="rounded-2xl p-5 relative"
        style={{
          background: 'linear-gradient(135deg, rgba(34,211,238,0.08), rgba(139,92,246,0.08)), #12152B',
          border: '1px solid rgba(34,211,238,0.3)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.7)',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="chip-cyan">✦ AI · ONBOARDING</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMinimized(true)}
              className="text-muted hover:text-white transition-colors p-1 text-xs font-mono-aurora tracking-wider uppercase"
              aria-label="Minimizar"
            >
              –
            </button>
            <button
              onClick={() => { localStorage.setItem(DISMISS_KEY, 'true'); setDismissed(true) }}
              className="text-dim hover:text-white transition-colors p-1"
              aria-label="Cerrar"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Progreso */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 h-1 rounded-full bg-line overflow-hidden">
            <div
              className="h-full aurora-gradient transition-all"
              style={{ width: `${(completedCount / visibleSteps.length) * 100}%` }}
            />
          </div>
          <span className="font-mono-aurora text-[10px] tracking-wider text-muted">
            {completedCount}/{visibleSteps.length}
          </span>
        </div>

        {/* Step actual */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-aurora-cyan/15 border border-aurora-cyan/30 flex items-center justify-center flex-shrink-0">
            <Icon size={18} className="text-aurora-cyan" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-base text-white leading-tight mb-1" style={{ letterSpacing: '-0.02em' }}>
              {currentStep.title}
            </h3>
            <p className="text-muted text-xs leading-relaxed">{currentStep.desc}</p>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={() => { setMinimized(true); navigate(currentStep.to) }}
          className="btn-primary-aurora w-full !py-2.5 !text-sm"
        >
          {currentStep.cta} <ArrowRight size={14} />
        </button>

        {/* Mini-lista de pasos */}
        <div className="mt-4 pt-3 border-t border-line-soft flex flex-col gap-1.5">
          {visibleSteps.map(s => {
            const done = checkedSteps[s.id]
            const active = s.id === currentStep.id
            return (
              <div key={s.id} className="flex items-center gap-2 text-xs">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  done ? 'bg-aurora-cyan' : active ? 'border-2 border-aurora-cyan' : 'border border-line'
                }`}>
                  {done && <Check size={10} className="text-ink" />}
                </div>
                <span className={done ? 'text-muted line-through' : active ? 'text-white' : 'text-dim'}>
                  {s.title}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
